import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Database, UserRound, HardHat, Calculator, BriefcaseBusiness, Check } from "lucide-react";
import { Reveal } from "./Reveal";

const ROLES = [
  {
    id: "role-selector-exec",
    label: "Owner",
    q: "What needs my attention?",
    before: "Before: ~40% of the week lost to administration",
    icon: BriefcaseBusiness,
    sees: [
      "Sold versus delivered across every account",
      "Cash position and outstanding invoices",
      "The three things needing attention today",
    ],
  },
  {
    id: "role-selector-pm",
    label: "Project Manager",
    q: "Which projects need action?",
    before: "Before: ~35% of the week lost to admin",
    icon: UserRound,
    sees: [
      "Live status of every active job",
      "Who is waiting on whom, right now",
      "Tomorrow's schedule risks, flagged early",
    ],
  },
  {
    id: "role-selector-site",
    label: "Site Supervisor",
    q: "What needs to happen today?",
    before: "Before: ~30% of the week lost to paperwork",
    icon: HardHat,
    sees: [
      "Today's tasks and site notes",
      "Change orders that affect the crew",
      "Sign-off checklists, nothing else",
    ],
  },
  {
    id: "role-selector-accounting",
    label: "Accounting",
    q: "What can I invoice — and what's outstanding?",
    before: "Before: ~25% of the week lost to chasing information",
    icon: Calculator,
    sees: [
      "Delivered work ready to bill, today",
      "Every overdue invoice, ranked by days",
      "Cash position, always current",
    ],
  },
];

export default function OneSourceModel() {
  const [active, setActive] = useState(0);
  const role = ROLES[active];

  return (
    <section className="py-20 lg:py-32 bg-white border-y border-slate-100 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-14 lg:mb-20">
          <Reveal className="col-span-12 lg:col-span-6">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-4">
              The model
            </p>
            <h2 className="font-display font-semibold tracking-tight leading-[1.12] text-brand-navy text-2xl sm:text-3xl lg:text-4xl">
              One truth. Different views.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="col-span-12 lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-base text-slate-600 leading-relaxed">
              Everyone sees the same underlying truth — through the lens of
              their own responsibility. Pick a seat and see what that person
              sees.
            </p>
          </Reveal>
        </div>

        <div data-testid="one-source-diagram" className="grid grid-cols-12 gap-8 lg:gap-12 items-center">
          <Reveal className="col-span-12 lg:col-span-5">
            <div className="relative rounded-3xl bg-brand-navy text-white p-8 sm:p-10 overflow-hidden">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-brand-teal/10" />
              <div className="absolute -bottom-20 -left-10 w-64 h-64 rounded-full bg-brand-teal/5" />
              <span className="relative w-12 h-12 rounded-2xl bg-brand-teal/15 text-brand-teal flex items-center justify-center mb-6">
                <Database size={22} />
              </span>
              <h3 className="relative font-display font-semibold text-2xl mb-3">
                One secure source of truth
              </h3>
              <p className="relative text-sm text-slate-300 leading-relaxed mb-8">
                Sales, projects, and finance reading from — and writing to — the
                same record. Entered once. Current always.
              </p>
              <div className="relative flex flex-wrap gap-2">
                {ROLES.map((r, i) => (
                  <button
                    key={r.id}
                    data-testid={r.id}
                    onClick={() => setActive(i)}
                    aria-pressed={active === i}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-all duration-300 ${
                      active === i
                        ? "bg-brand-teal text-white"
                        : "bg-white/10 text-slate-200 hover:bg-white/20"
                    }`}
                  >
                    <r.icon size={13} />
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="col-span-12 lg:col-span-6 lg:col-start-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={role.id}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="rounded-3xl border border-brand-tealborder bg-brand-tealsoft p-8 sm:p-10"
              >
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-brand-teal font-semibold mb-2">
                  The {role.label.toLowerCase()} asks
                </p>
                <h4 className="font-display font-medium text-xl text-brand-navy mb-6">
                  “{role.q}”
                </h4>
                <ul className="space-y-4">
                  {role.sees.map((item, i) => (
                    <motion.li
                      key={item}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + i * 0.08, duration: 0.4 }}
                      className="flex items-start gap-3 text-base text-slate-700"
                    >
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-brand-teal text-white flex items-center justify-center shrink-0">
                        <Check size={12} strokeWidth={3} />
                      </span>
                      {item}
                    </motion.li>
                  ))}
                </ul>
                <p className="mt-7 text-sm text-slate-500 leading-relaxed">
                  {role.before}. Now the system carries it — no clutter from
                  other roles, nothing chased, nothing extra.
                </p>
              </motion.div>
            </AnimatePresence>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
